<template>
  <div class="pycad-action-dropdown" ref="rootEl">
    <button
      ref="triggerRef"
      type="button"
      class="pycad-tool-btn pycad-action-dropdown__trigger"
      :class="{
        'pycad-tool-btn--labeled': !compact,
        'pycad-action-dropdown__trigger--compact': compact,
        'pycad-tool-btn--active': triggerActive,
        'pycad-tool-btn--disabled': disabled,
      }"
      :disabled="disabled"
      @click="toggleOpen"
    >
      <i
        :class="['mdi', displayIcon]"
        :style="{ fontSize: compact ? '14px' : '17px' }"
      ></i>
      <template v-if="compact">
        <i class="mdi mdi-chevron-down pycad-action-dropdown__chevron"></i>
      </template>
      <span
        v-else
        class="pycad-tool-btn__caption pycad-action-dropdown__caption"
      >
        <span class="pycad-action-dropdown__label">{{ label }}</span>
        <i class="mdi mdi-chevron-down pycad-action-dropdown__chevron"></i>
      </span>
    </button>

    <Teleport to="body">
      <transition name="pycad-dropdown-fade">
        <div
          v-if="open"
          ref="menuRef"
          class="pycad-action-dropdown__menu"
          :style="menuStyle"
        >
          <template
            v-for="(section, sectionIndex) in sections"
            :key="section.title || sectionIndex"
          >
            <div
              v-if="section.title"
              class="pycad-action-dropdown__menu-title"
              :class="{
                'pycad-action-dropdown__menu-title--spaced': sectionIndex > 0,
              }"
            >
              {{ section.title }}
            </div>
            <button
              v-for="item in section.items"
              :key="item.id"
              type="button"
              class="pycad-action-dropdown__item"
              :class="{
                'pycad-action-dropdown__item--active': activeItemId === item.id,
                'pycad-action-dropdown__item--disabled': item.disabled,
              }"
              :disabled="item.disabled"
              @click="onSelect(item)"
            >
              <i
                :class="['mdi', item.icon]"
                class="pycad-action-dropdown__item-icon"
              ></i>
              <span class="pycad-action-dropdown__item-label">{{
                item.label
              }}</span>
              <kbd
                v-if="item.shortcut"
                class="pycad-kbd pycad-action-dropdown__item-kbd"
              >
                {{ item.shortcut }}
              </kbd>
            </button>
          </template>
        </div>
      </transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { onClickOutside } from '@vueuse/core';
import { useFixedDropdownMenu } from '@/src/composables/useFixedDropdownMenu';

export interface ActionMenuItem {
  id: string;
  icon: string;
  label: string;
  disabled?: boolean;
  shortcut?: string;
}

export interface ActionMenuSection {
  title?: string;
  items: ActionMenuItem[];
}

interface Props {
  label: string;
  icon: string;
  sections: ActionMenuSection[];
  activeItemId?: string;
  disabled?: boolean;
  /** Icon + chevron in one row (matches adjacent 34px toolbar buttons). */
  compact?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  activeItemId: '',
  compact: false,
});

const emit = defineEmits<{
  (e: 'select', id: string): void;
}>();

const open = ref(false);
const rootEl = ref<HTMLElement>();
const triggerRef = ref<HTMLElement>();
const menuRef = ref<HTMLElement>();

const { menuStyle } = useFixedDropdownMenu(open, triggerRef);

const triggerActive = computed(() => !!props.activeItemId);

const displayIcon = computed(() => {
  if (!props.activeItemId) return props.icon;
  for (const section of props.sections) {
    const match = section.items.find((item) => item.id === props.activeItemId);
    if (match) return match.icon;
  }
  return props.icon;
});

onClickOutside(
  rootEl,
  () => {
    open.value = false;
  },
  { ignore: [menuRef] }
);

function toggleOpen() {
  if (props.disabled) return;
  open.value = !open.value;
}

function onSelect(item: ActionMenuItem) {
  if (item.disabled) return;
  emit('select', item.id);
  open.value = false;
}
</script>

<style scoped>
.pycad-action-dropdown {
  position: relative;
  display: inline-flex;
}

.pycad-action-dropdown__caption {
  display: inline-flex;
  align-items: center;
  gap: 1px;
}

.pycad-action-dropdown__label {
  line-height: 1;
}

.pycad-action-dropdown__chevron {
  font-size: 11px;
  opacity: 0.7;
  line-height: 1;
}

.pycad-action-dropdown__trigger--compact {
  flex-direction: row;
  gap: 1px;
  width: auto;
  min-width: 34px;
  height: 34px;
  padding: 0 5px;
}

.pycad-action-dropdown__menu {
  min-width: 190px;
  padding: 6px;
  background: var(--pycad-elevated, #161616);
  border: 1px solid var(--pycad-border, #282828);
  border-radius: var(--pycad-radius, 10px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.55);
}

.pycad-action-dropdown__menu-title {
  padding: 4px 10px 6px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--pycad-text-muted, #666);
}

.pycad-action-dropdown__menu-title--spaced {
  margin-top: 4px;
  padding-top: 8px;
  border-top: 1px solid var(--pycad-border, #282828);
}

.pycad-action-dropdown__item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 10px;
  font-size: 12.5px;
  font-family: inherit;
  color: var(--pycad-text-secondary, #b5b5b5);
  background: transparent;
  border: none;
  border-radius: var(--pycad-radius-sm, 6px);
  cursor: pointer;
  text-align: left;
  transition:
    background 0.12s ease,
    color 0.12s ease;
}

.pycad-action-dropdown__item:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.06);
  color: var(--pycad-text-primary, #fff);
}

.pycad-action-dropdown__item--active {
  color: var(--pycad-primary, #ff9900);
  background: var(--pycad-primary-dim, rgba(255, 153, 0, 0.12));
}

.pycad-action-dropdown__item--active:hover:not(:disabled) {
  color: var(--pycad-primary, #ff9900);
  background: var(--pycad-primary-dim, rgba(255, 153, 0, 0.16));
}

.pycad-action-dropdown__item--disabled {
  opacity: 0.4;
  cursor: default;
}

.pycad-action-dropdown__item-icon {
  font-size: 15px;
  width: 18px;
  text-align: center;
}

.pycad-action-dropdown__item-label {
  flex: 1;
  white-space: nowrap;
}

.pycad-action-dropdown__item-kbd {
  flex-shrink: 0;
}
</style>
